import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Betragsfeld mit deutscher Anzeige (10.000,50), intern number.
 * Nutzung: type="text" + kieneDeNumber (nicht type="number").
 */
@Directive({
    selector: 'input[kieneDeNumber]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KieneDeNumberDirective),
            multi: true,
        },
    ],
})
export class KieneDeNumberDirective implements ControlValueAccessor {
    private value: number | null = null;
    private onChange: (v: number | null) => void = () => undefined;
    private onTouched: () => void = () => undefined;
    private focused = false;

    constructor(private readonly el: ElementRef<HTMLInputElement>) {}

    writeValue(value: number | null | undefined): void {
        this.value = value == null || value === ('' as any) || Number.isNaN(Number(value)) ? null : Number(value);
        if (!this.focused) {
            this.el.nativeElement.value = this.format(this.value);
        }
    }

    registerOnChange(fn: (v: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.el.nativeElement.disabled = isDisabled;
    }

    @HostListener('focus')
    onFocus(): void {
        this.focused = true;
        this.el.nativeElement.value = this.value == null ? '' : this.toEditString(this.value);
        this.el.nativeElement.select();
    }

    @HostListener('blur')
    onBlur(): void {
        this.focused = false;
        const parsed = this.parse(this.el.nativeElement.value);
        this.value = parsed;
        this.el.nativeElement.value = this.format(parsed);
        this.onChange(parsed);
        this.onTouched();
    }

    @HostListener('input', ['$event'])
    onInput(ev: Event): void {
        const raw = (ev.target as HTMLInputElement).value;
        const parsed = this.parse(raw);
        this.value = parsed;
        this.onChange(parsed);
    }

    private format(n: number | null): string {
        if (n == null || Number.isNaN(n)) {
            return '';
        }
        return n.toLocaleString('de-DE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    }

    private toEditString(n: number): string {
        // Beim Tippen Komma als Dezimaltrenner, ohne Tausenderpunkt
        return String(n).replace('.', ',');
    }

    private parse(raw: string): number | null {
        const s = (raw || '').trim();
        if (!s) {
            return null;
        }
        // 10.000,50 oder 10000,5 oder 10000.5
        let normalized = s.replace(/\s/g, '');
        if (normalized.includes(',') && normalized.includes('.')) {
            normalized = normalized.replace(/\./g, '').replace(',', '.');
        } else if (normalized.includes(',')) {
            normalized = normalized.replace(',', '.');
        }
        const n = Number(normalized);
        return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
    }
}
